// ENG-4219 / ENG-3775 — choreography for the start-signal terminal state.
//
// runStartSignalLoop (start-signal-policy.ts) decides WHEN the loop is
// exhausted; this module owns WHAT happens then, as a pure dependency-injected
// function (sibling precedent: start-signal-policy.ts, watchdog-honesty.ts) so
// the ordering contract is unit-testable without React or module mocks:
//
//   1. capture EXACTLY ONE handled error for the whole run (ENG-4543 — the
//      transient attempts already went down the warning path);
//   2. flip the agent to the terminal offline state — and STOP if a fresher
//      truth (proxy state.started, status refetch) already moved it, so we
//      never show a "couldn't connect" UI or fire failure analytics for a
//      bridge that actually connected;
//   3. report the failure to PostHog as a HANDLED event with the error cause
//      attached (ENG-3775 decision: never an unhandled exception);
//   4. post the shared terminal-state UI (bridge-unreachable.tsx) with its
//      Retry affordance.
//
// Per rule: think-in-systems + reference-rules-in-comments (ai.knowledge/global).

/**
 * Machine-readable cause attached to the handled PostHog report. ENG-3775's
 * broker bootstrap/reconnect exhaustions will pass their own cause strings
 * through the same `cause` property — the UI stays cause-agnostic, only the
 * analytics row is separable.
 */
export const START_SIGNAL_UNREACHABLE_CAUSE = 'start_signal_exhausted';

/** Normalize the loop's unknown rejection into an Error without losing it. */
export const toTerminalError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error));

export type StartSignalTerminalDeps = {
  /** Handled capture — routed to the canonical logger (Sentry), exactly once. */
  readonly captureError: (error: Error) => void;

  /**
   * Flip the agent to the terminal offline state. Returns false when a
   * fresher truth already moved the agent — the terminal UI and analytics
   * are then skipped as dishonest.
   */
  readonly markUnreachable: () => boolean;

  /** The handled PostHog report (canonical bridge_connection_unavailable). */
  readonly trackUnavailable: (properties: { cause: string; error_message: string }) => void;

  /** Post the shared terminal-state UI (message + Retry). */
  readonly postTerminal: () => void;
};

/**
 * Settle an exhausted start-signal run to its user-visible terminal state.
 * See the module header for the ordering contract.
 */
export const settleStartSignalUnreachable = (
  error: unknown,
  deps: StartSignalTerminalDeps
): void => {
  const terminalError = toTerminalError(error);

  // (1) Exactly one handled capture per exhausted run (ENG-4543).
  deps.captureError(terminalError);

  // (2) Honesty gate: only surface failure UI/analytics for an agent that is
  // genuinely still stuck — a fresher status wins.
  if (!deps.markUnreachable()) {
    return;
  }

  // (3) Handled PostHog report with the cause attached (ENG-3775).
  deps.trackUnavailable({
    cause: START_SIGNAL_UNREACHABLE_CAUSE,
    error_message: terminalError.message
  });

  // (4) The user-facing terminal state with its Retry affordance.
  deps.postTerminal();
};
